import { useCallback, useMemo } from "react";

import useBulkActionsSelection from "components/BulkActions/useBulkActionsSelection";

import { getGroupKey, getSelectableRunIds, type RunsListPerCommit } from "./helpers";
import { SELECT_ALL_OPTIONS } from "./constants";

type UseStackRunsBulkSelectionProps = {
  runsListPerCommit: RunsListPerCommit;
  hasMore: boolean;
};

const useStackRunsBulkSelection = ({
  runsListPerCommit,
  hasMore,
}: UseStackRunsBulkSelectionProps) => {
  const {
    allSelected,
    selectedSet,
    selectItem,
    unselectItem,
    setSelectionState,
    onBulkResetAll,
    onBulkContinueWith,
    syncAllSelectedItems,
    syncSelectedItemsWithVisibleOnes,
  } = useBulkActionsSelection();

  const allVisibleSelectableIds = useMemo(
    () => getSelectableRunIds(runsListPerCommit),
    [runsListPerCommit]
  );

  const allVisibleSelected = useMemo(() => {
    if (allVisibleSelectableIds.size === 0) return false;

    for (const id of allVisibleSelectableIds) {
      if (!selectedSet.has(id)) {
        return false;
      }
    }
    return true;
  }, [allVisibleSelectableIds, selectedSet]);

  const allRunsAreSelected = allVisibleSelected && !hasMore;
  const hasSomeSelected = selectedSet.size > 0 && !allRunsAreSelected;

  // Compute which groups have all their runs selected
  const fullySelectedGroups = useMemo(() => {
    const selected = new Set<string>();
    for (const { hash, group, selectable, index } of runsListPerCommit) {
      if (!selectable || group.length === 0) continue;

      const allSelected = group.every((run) => selectedSet.has(run.id));
      if (allSelected) {
        selected.add(getGroupKey(hash, index));
      }
    }
    return selected;
  }, [runsListPerCommit, selectedSet]);

  // Handle toggle select all/unselect all
  const handleToggle = useCallback(() => {
    if (allRunsAreSelected || hasSomeSelected) {
      onBulkResetAll();
    } else {
      setSelectionState(allVisibleSelectableIds, !hasMore);
    }
  }, [
    allRunsAreSelected,
    hasSomeSelected,
    onBulkResetAll,
    allVisibleSelectableIds,
    hasMore,
    setSelectionState,
  ]);

  const handleSelectOption = useCallback(
    (optionId: string) => {
      const option = SELECT_ALL_OPTIONS.find((opt) => opt.id === optionId);
      if (!option) return;

      const selectedIds = getSelectableRunIds(runsListPerCommit, option.filter);
      const isSelectingAll = !option.filter;

      setSelectionState(selectedIds, isSelectingAll && !hasMore);
    },
    [runsListPerCommit, hasMore, setSelectionState]
  );

  return {
    allSelected,
    selectedSet,
    allRunsAreSelected,
    hasSomeSelected,
    fullySelectedGroups,

    selectItem,
    unselectItem,
    handleToggle,
    handleSelectOption,
    onBulkResetAll,
    onBulkContinueWith,
    syncAllSelectedItems,
    syncSelectedItemsWithVisibleOnes,
  };
};

export default useStackRunsBulkSelection;
